"use client";
import React, { useState } from "react";
import {
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, IconButton, 
  TextField, InputAdornment, Stack, Container, Button,
  Tooltip, TablePagination, CircularProgress, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Grid
} from "@mui/material";
import {
  Search, Visibility, FilterList, FileDownload,
  AccountCircle, EventNote, ArrowBack, Close, Assignment,
  Person, Verified, Badge, History, Description, FactCheck
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { QK } from "../../../base/qk";
import { getCarePlanReviewsByClient } from "../../../api/services/CarePlan/review";

const ReviewedCarePlans = () => {
  const navigate = useNavigate();
  const { ClientID } = useParams();
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReview, setSelectedReview] = useState(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery(
    [QK.carePlanReviews, ClientID, page, rowsPerPage], 
    () => getCarePlanReviewsByClient(ClientID, null, true, page, rowsPerPage), 
    { enabled: !!ClientID, keepPreviousData: true }
  );

  const reviews = data?.results || [];
  const totalCount = data?.count || 0;

  const handleOpenDialog = (review) => {
    setSelectedReview(review);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedReview(null);
  };

  const filteredReviews = searchTerm.trim() 
    ? reviews.filter(review => 
        review.reviewer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.care_plan_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : reviews;

  const formatDateUK = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getStatusChip = (status) => {
    const colors = {
      'ACTIVE': { bg: '#e8f5e9', text: '#2e7d32' },
      'MONITOR': { bg: '#fff3e0', text: '#ed6c02' },
      'INACTIVE': { bg: '#ffebee', text: '#d32f2f' }
    };
    const style = colors[status] || { bg: '#f1f5f9', text: '#475569' };
    return <Chip label={status} size="small" sx={{ bgcolor: style.bg, color: style.text, fontWeight: 700, borderRadius: '6px' }} />;
  };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", pb: 5 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fff', py: 3 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
               <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f1f5f9' }}><ArrowBack /></IconButton>
               <Box>
                <Typography variant="h5" fontWeight={800} color="#1e293b">Care Plan Reviews</Typography>
                <Typography variant="body2" color="textSecondary">Historical evaluations for Client ID: {ClientID}</Typography>
               </Box>
            </Stack>
            <Button startIcon={<FileDownload />} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Export</Button>
          </Stack>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Stack direction="row" p={2.5} spacing={2} alignItems="center" justifyContent="space-between">
            <TextField
              placeholder="Search by reviewer or care plan..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 350, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
            />
          </Stack>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Reviewer</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Care Plan</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Dependency</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Review Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 10 }}><CircularProgress size={30} /></TableCell></TableRow>
                ) : filteredReviews.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{getStatusChip(row.care_plan_status)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{row.reviewer_name?.charAt(0)}</Avatar>
                        <Typography variant="body2" fontWeight={500}>{row.reviewer_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.care_plan_name}</Typography>
                        <Typography variant="caption" color="textSecondary">ID: {row.care_plan}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1 }}>{row.dependency_level}</Typography></TableCell>
                    <TableCell>{formatDateUK(row.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenDialog(row)} sx={{ color: '#6366f1', bgcolor: '#eef2ff' }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={totalCount} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} onRowsPerPageChange={(e) => {setRowsPerPage(parseInt(e.target.value, 10)); setPage(0);}} />
        </Paper>
      </Container>

      {/* DETAILED VIEW DIALOG */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}>
        <DialogTitle sx={{ m: 0, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#6366f1' }}><Assignment /></Avatar>
            <Box>
                <Typography variant="h6" fontWeight={800}>Full Review Record</Typography>
                <Typography variant="caption" color="textSecondary">Created: {formatDateUK(selectedReview?.created_at)}</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseDialog}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 4 }}>
          {selectedReview && (
            <Grid container spacing={3}>
              {/* Section 1: Entities */}
              <Grid item xs={12} md={4}>
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="textSecondary" fontWeight={700}>CLIENT NAME (ID: {selectedReview.client})</Typography>
                    <Typography variant="body1" fontWeight={700} color="#1e293b">{selectedReview.client_name}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="textSecondary" fontWeight={700}>REVIEWER NAME</Typography>
                    <Typography variant="body1" fontWeight={700} color="#1e293b">{selectedReview.reviewer_name}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="textSecondary" fontWeight={700}>CARE PLAN NAME (ID: {selectedReview.care_plan})</Typography>
                    <Typography variant="body1" fontWeight={700} color="#1e293b">{selectedReview.care_plan_name}</Typography>
                </Stack>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* Section 2: Review Specifics */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>CARE PLAN STATUS</Typography>
                <Box mt={0.5}>{getStatusChip(selectedReview.care_plan_status)}</Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>DEPENDENCY LEVEL</Typography>
                <Typography variant="body1" fontWeight={600} mt={0.5}>{selectedReview.dependency_level}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>NEXT REVIEW DATE</Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <EventNote fontSize="small" color="primary" />
                    <Typography variant="body1" fontWeight={700} color="primary">{formatDateUK(selectedReview.next_review_date)}</Typography>
                </Stack>
              </Grid>

              {/* Section 3: Consent & Summary Metadata */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>ROLE / HOW GIVEN</Typography>
                <Typography variant="body2" fontWeight={500} mt={0.5}>{selectedReview.role_how_given}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>CONSENT PERSON</Typography>
                <Typography variant="body2" fontWeight={500} mt={0.5}>{selectedReview.consent_person_name}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>SUMMARY HEADING</Typography>
                <Typography variant="body2" fontWeight={500} mt={0.5}>{selectedReview.summary}</Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* Section 4: Evaluation Summary */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <FactCheck color="action" fontSize="small" />
                    <Typography variant="caption" color="textSecondary" fontWeight={700}>DETAILED EVALUATION SUMMARY</Typography>
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {selectedReview.evaluation_summary}
                    </Typography>
                </Paper>
              </Grid>

              {/* Section 5: Signature & Verification */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>SIGNATURE VERIFICATION</Typography>
                <Stack direction="row" spacing={1} mt={1} alignItems="center">
                    {selectedReview.is_signed ? (
                        <Chip icon={<Verified />} label="Electronically Signed" color="success" size="small" />
                    ) : (
                        <Chip label="Not Signed" variant="outlined" color="error" size="small" />
                    )}
                </Stack>
                <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
                  {selectedReview.review_signature ? (
                    <img src={selectedReview.review_signature} alt="Signature" style={{ maxHeight: '100px', maxWidth: '100%' }} />
                  ) : "No Signature Image Available"}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
          <Button variant="contained" sx={{ borderRadius: 2, bgcolor: '#0f172a' }}>Print Report</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewedCarePlans;